import { TierIIReportKind } from "encamp-prisma/generated/client/client";
import { AlternateId, PortalCredential } from "generated-graphql/graphql";
import { getEncampyLoginAutomation } from "util/encampy/portalLogin";
import { useEncampyLaunchData } from "../../hooks/useEncampyLaunchData";
import {
  EncampyAutomationLauncher,
  EncampyAutomationLauncherProps,
} from "./AutomationLauncher";

export interface EncampyPortalLoginLauncherProps
  extends Omit<EncampyAutomationLauncherProps, "launchData"> {
  portalCredential?: PortalCredential | null;
  facilityAlternateIds?: AlternateId[] | null;
  reportingYear?: number;
  reportKind?: TierIIReportKind;
}

export function EncampyPortalLoginLauncher({
  portalCredential,
  facilityAlternateIds,
  reportingYear,
  reportKind,
  ...automationLauncherProps
}: EncampyPortalLoginLauncherProps) {
  const { launchData, isLoading, error } = useEncampyLaunchData(
    portalCredential?.url,
    portalCredential
      ? getEncampyLoginAutomation(
          portalCredential,
          facilityAlternateIds,
          reportingYear,
          reportKind
        )
      : null,
    [portalCredential, facilityAlternateIds, reportingYear, reportKind]
  );

  // If we're still generating launch data, show disabled state
  if (isLoading) {
    return (
      <EncampyAutomationLauncher
        {...automationLauncherProps}
        launchData={{ url: "", tasks: [] }}
        buttonText="Loading..."
        disabled={true}
      />
    );
  }

  // If we have an error generating launch data, show disabled state with error
  if (error || !launchData) {
    const handleErrorClick = () => {
      automationLauncherProps.onError?.(
        error || "Failed to generate automation data"
      );
    };

    return (
      <EncampyAutomationLauncher
        {...automationLauncherProps}
        launchData={{ url: "", tasks: [] }}
        disabled={true}
        disabledReason={error || "Failed to generate automation data"}
        onError={handleErrorClick}
      />
    );
  }

  // Normal case: pass through all props to EncampyAutomationLauncher with generated launch data
  return (
    <EncampyAutomationLauncher
      {...automationLauncherProps}
      launchData={launchData}
    />
  );
}
